import { computed, inject, Injectable, signal } from '@angular/core';
import { Router } from '@angular/router';
import { TranslocoService } from '@jsverse/transloco';
import { CustomerServiceLoanCategory } from '@ld.consumer-portal/constants';
import {
  clearOriginationSelection,
  getLoanTypeFromProfile,
  Loan,
  LOAN_STORAGE_KEYS,
  LoanResponse,
  LoanTransferStatus,
  SelectedLoanData,
  UserProfile,
} from '@ld.consumer-portal/models';
import { LoggingService } from './logging.service';

export interface LoanSelectorAccount {
  id: string;
  type: 'MORTGAGE' | 'HELOC' | 'HOME_EQUITY';
  address: string;
  accountNumberLast4: string;
}

@Injectable({
  providedIn: 'root',
})
export class LoanSelectionService {
  private readonly router = inject(Router);
  private readonly translocoService = inject(TranslocoService);
  private readonly loggingService = inject(LoggingService);

  private readonly LOAN_TRANSFERRED_DAYS_LIMIT = 180;

  private readonly _allLoanResponses = signal<LoanResponse[]>([]);
  readonly allLoanResponses = this._allLoanResponses.asReadonly();

  private readonly _userProfile = signal<UserProfile | null>(null);

  readonly availableLoans = computed(() => this._allLoanResponses().map((lr) => lr.loan));

  readonly hasMultipleLoans = computed(() => this._allLoanResponses().length > 1);

  readonly loanCount = computed(() => this._allLoanResponses().length);

  readonly hasMortgageOrHeloc = computed(() => {
    const profile = this._userProfile();
    return this._allLoanResponses().some((lr) => {
      const type = this.getLoanTypeShortNameWithProfile(lr.loan, profile);
      return type === 'MORTGAGE' || type === 'HELOC';
    });
  });

  readonly loanSelectorAccounts = computed<LoanSelectorAccount[]>(() => {
    const profile = this._userProfile();
    return this._allLoanResponses().map((lr) => ({
      id: lr.loan.loanNumber,
      type: this.getLoanTypeShortNameWithProfile(lr.loan, profile),
      address: this.getFullAddress(lr.loan),
      accountNumberLast4: lr.loan.loanNumber.slice(-4),
    }));
  });

  readonly selectedLoanAddress = computed(() => {
    const loan = this.selectedLoan();
    return loan ? this.getFullAddress(loan) : null;
  });

  readonly selectedLoanType = computed(() => {
    const loan = this.selectedLoan();
    return loan ? this.getLoanTypeShortName(loan) : null;
  });

  private readonly _selectedLoanResponse = signal<LoanResponse | null>(null);
  readonly selectedLoanResponse = this._selectedLoanResponse.asReadonly();

  readonly selectedLoan = computed(() => this._selectedLoanResponse()?.loan ?? null);

  readonly selectedLoanNumber = computed(() => this.selectedLoan()?.loanNumber ?? null);

  readonly selectedLoanTransferStatus = computed(() => {
    const loan = this.selectedLoan();
    return loan ? this.getTransferStatus(loan) : null;
  });

  readonly areFeaturesDisabled = computed(() => this.selectedLoanTransferStatus()?.areFeaturesDisabled ?? false);

  readonly isHeloc = computed(() => {
    const loanType = this.selectedLoanType();
    return loanType === 'HELOC';
  });

  readonly isVaLoan = computed(() => {
    const loan = this.selectedLoan();
    return loan?.productType?.toUpperCase() === 'VA';
  });

  readonly customerServiceLoanCategory = computed((): CustomerServiceLoanCategory => {
    if (this.isVaLoan()) return 'VA';
    if (this.isHeloc()) return 'HELOC';
    return 'MORTGAGE';
  });

  initialize(): void {
    this.loadPersistedSelection();
  }

  private loadPersistedSelection(): void {
    try {
      const storedDetails = localStorage.getItem(LOAN_STORAGE_KEYS.SELECTED_LOAN_DETAILS);
      if (storedDetails) {
        JSON.parse(storedDetails);
      }
    } catch (error) {
      this.loggingService.logException(error, 'LoanSelection: Failed to load persisted selection');
    }
  }

  selectLoan(loanResponse: LoanResponse): void {
    const loan = loanResponse.loan;

    this._selectedLoanResponse.set(loanResponse);

    this.persistSelection(loan);
  }

  selectLoanByNumber(loanNumber: string, loanResponses: LoanResponse[]): boolean {
    const loanResponse = loanResponses.find((lr) => lr.loan.loanNumber === loanNumber);

    if (loanResponse) {
      this.selectLoan(loanResponse);
      return true;
    }

    this.loggingService.logWarning(`LoanSelection: Loan not found: ${loanNumber}`);
    return false;
  }

  selectLoanById(loanId: string): boolean {
    const loanResponses = this._allLoanResponses();
    const loanResponse = loanResponses.find((lr) => lr.loan.loanNumber === loanId);

    if (loanResponse) {
      this.selectLoan(loanResponse);
      return true;
    }

    this.loggingService.logWarning(`LoanSelection: Loan not found in available loans: ${loanId}`);
    return false;
  }

  setSelectedLoanResponse(loanResponse: LoanResponse): void {
    this._selectedLoanResponse.set(loanResponse);
    this.persistSelection(loanResponse.loan);
  }

  private persistSelection(loan: Loan): void {
    const isHeloc = this.getLoanTypeShortNameWithProfile(loan, this._userProfile()) === 'HELOC';

    localStorage.setItem(LOAN_STORAGE_KEYS.SELECTED_LOAN, JSON.stringify(loan.loanNumber));

    localStorage.setItem(LOAN_STORAGE_KEYS.IS_HELOC, JSON.stringify(isHeloc));

    if (isHeloc) {
      localStorage.setItem(LOAN_STORAGE_KEYS.HELOC_LOAN_NUMBER, JSON.stringify(loan.loanNumber));
    }

    const loanDetails: SelectedLoanData = {
      loanNumber: loan.loanNumber,
      encryptionValue: loan.lnKey || '',
      isHeloc,
      address: loan.property ? `${loan.property.address}, ${loan.property.city}, ${loan.property.state}` : undefined,
      productType: loan.productType,
    };
    localStorage.setItem(LOAN_STORAGE_KEYS.SELECTED_LOAN_DETAILS, JSON.stringify(loanDetails));
  }

  getPersistedLoanNumber(): string | null {
    try {
      const stored = localStorage.getItem(LOAN_STORAGE_KEYS.SELECTED_LOAN);
      return stored ? JSON.parse(stored) : null;
    } catch {
      return null;
    }
  }

  getPersistedLoanDetails(): SelectedLoanData | null {
    try {
      const stored = localStorage.getItem(LOAN_STORAGE_KEYS.SELECTED_LOAN_DETAILS);
      return stored ? JSON.parse(stored) : null;
    } catch {
      return null;
    }
  }

  clearSelection(): void {
    this._selectedLoanResponse.set(null);
    clearOriginationSelection();
    this._allLoanResponses.set([]);
    this._userProfile.set(null);

    localStorage.removeItem(LOAN_STORAGE_KEYS.SELECTED_LOAN);
    localStorage.removeItem(LOAN_STORAGE_KEYS.SELECTED_LOAN_DETAILS);
    localStorage.removeItem(LOAN_STORAGE_KEYS.IS_HELOC);
    localStorage.removeItem(LOAN_STORAGE_KEYS.HELOC_LOAN_NUMBER);
    localStorage.removeItem(LOAN_STORAGE_KEYS.ENCRYPTION_VALUE);
    this.clearPiitSessionState();
  }

  getTransferStatus(loan: Loan): LoanTransferStatus {
    const isPaidInFull = loan.isPaidInFull ?? false;
    const isTransferredToCenlar = loan.isTransferredToCenlar ?? false;
    const isTransferredToThirdParty = loan.isTransferredToThirdParty ?? false;
    const isEarlyTransferToCenlar = loan.isEarlyTransferToCenlar ?? false;
    const isPIITServiceTransfer = loan.isPIITServiceTransfer ?? false;
    const loanTransferredDays = loan.loanTransferredDays ?? null;

    const isTransferredOrPaidOff = isPaidInFull || isTransferredToCenlar || isTransferredToThirdParty;
    const isPastThreshold = loanTransferredDays !== null && loanTransferredDays >= this.LOAN_TRANSFERRED_DAYS_LIMIT;
    const areFeaturesDisabled = isTransferredOrPaidOff && isPastThreshold;

    let disabledReason: string | null = null;
    if (areFeaturesDisabled) {
      if (isPaidInFull) {
        disabledReason = this.translocoService.translate('servicesStatusLoanPaidInFullShort');
      } else if (isTransferredToCenlar) {
        disabledReason = this.translocoService.translate('servicesStatusLoanTransferredCenlar');
      } else if (isTransferredToThirdParty) {
        disabledReason = this.translocoService.translate('servicesStatusLoanTransferredToServicer', {
          servicer: loan.newServicer || this.translocoService.translate('servicesLabelsNewServicerFallback'),
        });
      }
    }

    return {
      isPaidInFull,
      isTransferredToCenlar,
      isTransferredToThirdParty,
      isEarlyTransferToCenlar,
      isPIITServiceTransfer,
      loanTransferredDays,
      areFeaturesDisabled,
      disabledReason,
    };
  }

  isFeatureAvailable(loan: Loan, feature: 'payments' | 'documents' | 'autopay' | 'paperless' | 'contact'): boolean {
    const status = this.getTransferStatus(loan);

    switch (feature) {
      case 'payments':
        return !status.isPaidInFull && !status.isTransferredToCenlar && !status.isTransferredToThirdParty;

      case 'documents':
        return !status.areFeaturesDisabled;

      case 'autopay':
        return (
          !status.isPaidInFull && !status.isTransferredToCenlar && !status.isTransferredToThirdParty && loan.isActive
        );

      case 'paperless':
        return !status.isPaidInFull && !status.isTransferredToCenlar && !status.isTransferredToThirdParty;

      case 'contact':
        return (
          !status.isPaidInFull && !status.isTransferredToCenlar && !status.isTransferredToThirdParty && loan.isActive
        );

      default:
        return !status.areFeaturesDisabled;
    }
  }

  isContactAndHelpCardEnabled(loan: Loan): boolean {
    return !loan.isTransferredToCenlar && !loan.isTransferredToThirdParty && loan.isActive && !loan.isPaidInFull;
  }

  shouldShowContactReview(loan: Loan): boolean {
    return (
      !loan.isTransferredToCenlar &&
      !loan.isTransferredToThirdParty &&
      !loan.isPIITServiceTransfer &&
      !loan.isEarlyTransferToCenlar
    );
  }

  isLoanHeloc(loan: Loan): boolean {
    return this.getLoanTypeShortNameWithProfile(loan, this._userProfile()) === 'HELOC';
  }

  getLoanTypeDisplayName(loan: Loan): string {
    const productType = loan.productType?.toLowerCase();
    switch (productType) {
      case 'heloc':
        return this.translocoService.translate('servicesLabelsLoanTypeHelocFull');
      case 'homeequity':
      case 'home_equity':
        return this.translocoService.translate('servicesLabelsLoanTypeHomeEquityFull');
      default:
        return this.translocoService.translate('tooltipMonthlyMortgageTitle');
    }
  }

  setAllLoanResponses(loanResponses: LoanResponse[], userProfile?: UserProfile | null): void {
    if (userProfile !== undefined) {
      this._userProfile.set(userProfile);
    }
    this._allLoanResponses.set(loanResponses);
  }

  private readonly HELOC_ONLY_ROUTES = ['request-funds'];

  private readonly MORTGAGE_ONLY_ROUTES = ['payoff-quote'];

  navigateToAccount(accountId: string): void {
    this.router.navigate(['/loans', accountId, 'details']);
  }

  private getLoanTypeForAccount(accountId: string): 'HELOC' | 'HOME_EQUITY' | 'MORTGAGE' {
    const loanResponse = this._allLoanResponses().find((lr) => lr.loan.loanNumber === accountId);
    if (loanResponse) {
      return this.getLoanTypeShortNameWithProfile(loanResponse.loan, this._userProfile());
    }
    return 'MORTGAGE';
  }

  private getFullAddress(loan: Loan): string {
    const property = loan.property;
    if (!property) return '';
    return `${property.address}, ${property.city}, ${property.state} ${property.zip}`.trim();
  }

  getLoanTypeShortName(loan: Loan): 'HELOC' | 'HOME_EQUITY' | 'MORTGAGE' {
    return this.getLoanTypeShortNameWithProfile(loan, this._userProfile());
  }

  private getLoanTypeShortNameWithProfile(
    loan: Loan,
    profile: UserProfile | null,
  ): 'HELOC' | 'HOME_EQUITY' | 'MORTGAGE' {
    if (profile) {
      const profileLoanType = getLoanTypeFromProfile(loan.loanNumber, profile).toUpperCase();
      if (profileLoanType === 'HELOC') {
        return 'HELOC';
      }
      if (profileLoanType === 'HOME_EQUITY' || profileLoanType === 'HOMEEQUITY') {
        return 'HOME_EQUITY';
      }
      if (profileLoanType) {
        return 'MORTGAGE';
      }
    }

    const productType = loan.productType?.toLowerCase();
    switch (productType) {
      case 'heloc':
        return 'HELOC';
      case 'homeequity':
      case 'home_equity':
        return 'HOME_EQUITY';
      default:
        return 'MORTGAGE';
    }
  }

  isAutopayAvailable(loan: Loan): boolean {
    return this.isFeatureAvailable(loan, 'autopay');
  }

  getAutopayIneligibilityReason(loan: Loan): string | null {
    const status = this.getTransferStatus(loan);

    if (status.isPaidInFull) {
      return this.translocoService.translate('servicesErrorsAutopayPaidInFull');
    }

    if (status.isTransferredToCenlar || status.isTransferredToThirdParty) {
      return this.translocoService.translate('servicesErrorsAutopayTransferred');
    }

    if (!loan.isActive) {
      return this.translocoService.translate('servicesErrorsAutopayInactive');
    }

    return null;
  }

  isOneTimePaymentAvailable(loan: Loan): boolean {
    if (!this.isFeatureAvailable(loan, 'payments')) {
      return false;
    }

    return true;
  }

  shouldUseHelocPaymentGateway(loan: Loan): boolean {
    return this.isLoanHeloc(loan);
  }

  getLoanNumberForApi(): string | null {
    const loan = this.selectedLoan();
    if (!loan) return null;

    if (this.isLoanHeloc(loan)) {
      try {
        const helocNumber = localStorage.getItem(LOAN_STORAGE_KEYS.HELOC_LOAN_NUMBER);
        if (helocNumber) {
          return JSON.parse(helocNumber);
        }
      } catch {
        // Fall through to default
      }
    }

    return loan.loanNumber;
  }

  getLoanNumberForLogging(): string | null {
    try {
      const isHeloc = localStorage.getItem(LOAN_STORAGE_KEYS.IS_HELOC);
      if (isHeloc && JSON.parse(isHeloc) === true) {
        const helocNumber = localStorage.getItem(LOAN_STORAGE_KEYS.HELOC_LOAN_NUMBER);
        return helocNumber ? JSON.parse(helocNumber) : null;
      }
      const loanNumber = localStorage.getItem(LOAN_STORAGE_KEYS.SELECTED_LOAN);
      return loanNumber ? JSON.parse(loanNumber) : null;
    } catch {
      return null;
    }
  }

  getEncryptionValue(): string | null {
    const loan = this.selectedLoan();
    return loan?.lnKey ?? null;
  }

  isSelectedLoanActive(): boolean {
    return this.selectedLoan()?.isActive ?? false;
  }

  getNewServicerInfo(): { name: string; phone: string } | null {
    const loan = this.selectedLoan();
    if (!loan || (!loan.isTransferredToCenlar && !loan.isTransferredToThirdParty)) {
      return null;
    }

    return {
      name: loan.newServicer || this.translocoService.translate('servicesLabelsNewServicerFallback'),
      phone: loan.newServicerPhone || '',
    };
  }

  getHelocInfo(): {
    isHeloc: boolean;
    displayName: string;
    shortName: 'HELOC' | 'HOME_EQUITY' | 'MORTGAGE';
    canEnrollAutopay: boolean;
    autopayIneligibilityReason: string | null;
    useHelocPaymentGateway: boolean;
  } | null {
    const loan = this.selectedLoan();
    if (!loan) return null;

    return {
      isHeloc: this.isLoanHeloc(loan),
      displayName: this.getLoanTypeDisplayName(loan),
      shortName: this.getLoanTypeShortName(loan),
      canEnrollAutopay: this.isAutopayAvailable(loan),
      autopayIneligibilityReason: this.getAutopayIneligibilityReason(loan),
      useHelocPaymentGateway: this.shouldUseHelocPaymentGateway(loan),
    };
  }

  shouldShowPiitTransferModal(loan: Loan): boolean {
    if (!loan.isPIITServiceTransfer) {
      return false;
    }

    const dismissedLoans = this.getPiitDismissedLoans();
    if (dismissedLoans.includes(loan.loanNumber)) {
      return false;
    }

    const currentlyProcessedLoan = localStorage.getItem(LOAN_STORAGE_KEYS.PIIT_CURRENT_LOAN);
    if (currentlyProcessedLoan === loan.loanNumber) {
      return false;
    }

    return true;
  }

  private getPiitDismissedLoans(): string[] {
    try {
      const stored = localStorage.getItem(LOAN_STORAGE_KEYS.PIIT_DISMISSED_LOANS);
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  }

  dismissPiitModalForLoan(loanNumber: string): void {
    const dismissedLoans = this.getPiitDismissedLoans();
    if (!dismissedLoans.includes(loanNumber)) {
      dismissedLoans.push(loanNumber);
      localStorage.setItem(LOAN_STORAGE_KEYS.PIIT_DISMISSED_LOANS, JSON.stringify(dismissedLoans));
    }
    localStorage.setItem(LOAN_STORAGE_KEYS.PIIT_CURRENT_LOAN, loanNumber);
  }

  remindPiitModalLater(loanNumber: string): void {
    localStorage.setItem(LOAN_STORAGE_KEYS.PIIT_CURRENT_LOAN, loanNumber);
  }

  getPiitTransferInfo(loan: Loan): { newServicer: string; transferDate: Date } | null {
    if (!loan.isPIITServiceTransfer) {
      return null;
    }

    return {
      newServicer: loan.newServicer || this.translocoService.translate('servicesLabelsNewServicerFallback'),
      transferDate: loan.transferDate ? new Date(loan.transferDate) : new Date(),
    };
  }

  clearPiitSessionState(): void {
    localStorage.removeItem(LOAN_STORAGE_KEYS.PIIT_CURRENT_LOAN);
  }
}
